import React, { FC, useContext, useState } from 'react'
import _ from 'lodash'
import { Trans, t } from '@lingui/macro'
import './CampaignAudience.less'
import { ManageCampaignContext, SingleCampaign } from '../../ManageCampaignPage'
import { SaveCampaignFiltersButton } from '../SaveFiltersButton'
import CampaignAudienceTable from './CampaignAudienceTable'
import CampaignFiltersList from './CampaignFiltersList'
import { AddAudienceFilterButton } from './AddAudienceFilterButton'
import { CampaignAudienceCheckPreview } from '../../../../components/common/audience/AudienceCheckPreview'
import IconFont from '../../../../components/common/IconFont'
import CampaignImmediateAudienceTable from './CampaignImmediateAudienceTable'
import {
  CampaignScheduleEnum,
  CampaignFilterTypeEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { NbSp } from 'front-end/src/utils/NbSp'
import IconDoc from 'front-end/src/components/common/icon/IconDoc'
import IconLayers from 'front-end/src/components/common/icon/IconLayers'
import IconClock from 'front-end/src/components/common/icon/IconClock'
import IconMap from 'front-end/src/components/common/icon/IconMap'
import IconBadge from 'front-end/src/components/common/icon/IconBadge'
import IconPeople from 'front-end/src/components/common/icon/IconPeople'
import { ButtonOutline } from 'front-end/src/components'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import IconCalendar from 'front-end/src/components/common/icon/IconCalendar'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

export const CampaignAudience: FC = () => {
  const { campaign } = useContext(ManageCampaignContext)
  const { session } = usePublicApiSession()
  const { featureFlags } = useFeatureFlags()
  const [filters, setFilters] = useState(campaign.filters)
  const [showAudienceCheckPreview, setShowAudienceCheckPreview] =
    useState<boolean>(false)
  const isDirty = !_.isEqual(filters, campaign.filters)
  const { wasCampaignDeactivated, schedule_type, is_active } = campaign

  const getFiltersChangedMessage = () => {
    if (schedule_type === CampaignScheduleEnum.IMMEDIATE) {
      return isDirty
        ? t`You have made changes to your audience filters. Preview to see users that fit filters in this campaign, or save to apply your changes.`
        : t`Click on preview audience button to see users that fit your filters setup.`
    } else {
      return isDirty
        ? t`You have made changes to your audience filters. Preview to see who will be added/removed, or save to apply your changes.`
        : t`Your audience will be populated once you activate the campaign.`
    }
  }

  if (
    wasCampaignDeactivated &&
    schedule_type !== CampaignScheduleEnum.IMMEDIATE
  ) {
    return (
      <div className="CampaignAudience">
        <CampaignAudienceTable campaignId={campaign.id} />
      </div>
    )
  }

  if (
    (is_active || wasCampaignDeactivated) &&
    schedule_type === CampaignScheduleEnum.IMMEDIATE
  ) {
    return (
      <div className="CampaignAudience">
        <CampaignImmediateAudienceTable campaignId={campaign.id} />
      </div>
    )
  }

  let filterTypeCards = {
    [CampaignFilterTypeEnum.value_comparison]: (
      <>
        <IconFont type="user-following" />
        <h3>
          <Trans>Attribute</Trans>
        </h3>
        <small>
          <Trans>Find people with a specific attribute</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.duration_comparison]: (
      <>
        <IconClock />
        <h3>
          <Trans>Last active</Trans>
        </h3>
        <small>
          <Trans>Find people who have not been active</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.topic_enrolment]: (
      <>
        <IconDoc boxSize="20px" />
        <h3>
          <Trans>Resource</Trans>
        </h3>
        <small>
          <Trans>Find people by resource enrolment</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.pathway_enrolment]: (
      <>
        <IconMap />
        <h3>
          <Trans>Pathway</Trans>
        </h3>
        <small>
          <Trans>Find people by pathway enrolment</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.certification_enrolment]: (
      <>
        <IconBadge />
        <h3>
          <Trans>Certification</Trans>
        </h3>
        <small>
          <Trans>Find people by certification enrolment</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.group]: (
      <>
        <IconPeople />
        <h3>
          <Trans>Group</Trans>
        </h3>
        <small>
          <Trans>Select people who belong to a group</Trans>
        </small>
      </>
    ),
    [CampaignFilterTypeEnum.workspace]: (
      <>
        <IconLayers />
        <h3>
          <Trans>Workspace</Trans>
        </h3>
        <small>
          <Trans>Members of a workspace</Trans>
        </small>
      </>
    )
  }

  if (featureFlags.campaigns_event_filters) {
    filterTypeCards[CampaignFilterTypeEnum.event_invitation] = (
      <>
        <IconCalendar />
        <h3>
          <Trans>Event</Trans>
        </h3>
        <small>
          <Trans>Find people by event enrolment</Trans>
        </small>
      </>
    )
  }

  return (
    <div className="CampaignAudience">
      <div className="CampaignAudience__header">
        <h2>
          <Trans>Define your target audience</Trans>
          <span>
            <Trans>
              Add filters below to refine your target audience for this campaign
            </Trans>
          </span>
        </h2>
        <div className="CampaignAudience__header-controls">
          {isDirty && (
            <ButtonOutline
              onClick={() => {
                setFilters(campaign.filters)
              }}
            >
              <Trans>Cancel</Trans>
            </ButtonOutline>
          )}
        </div>
      </div>

      <div className="CampaignAudience__filters">
        <CampaignFiltersList
          filters={filters}
          onUpdate={(updatedFilters) => {
            setFilters(updatedFilters)
          }}
        />
        <AddAudienceFilterButton
          onAdd={(filter) => {
            filter.created_at = new Date().toISOString()
            filter.created_by = session?.user.id as number
            const updatedFilters = [...filters, filter]
            setFilters(updatedFilters as SingleCampaign['filters'])
          }}
          // @ts-ignore
          filterTypeCardsMap={filterTypeCards}
        />
      </div>
      {filters.length === 0 && (
        <div className="CampaignAudience__filters-save-cta CampaignAudience__filters-save-cta--warning">
          <span className="CampaignAudience__filters-save-cta__icon">
            &#9888;
          </span>
          <NbSp />
          <Trans>All campaigns must have at least one audience filter</Trans>
        </div>
      )}
      {filters.length > 0 && (isDirty || campaign.is_active === false) && (
        <div className="CampaignAudience__filters-cta-wrapper">
          <div className="CampaignAudience__filters-save-cta">
            <span className="CampaignAudience__filters-audience-check-description">
              {getFiltersChangedMessage()}
            </span>

            <ButtonOutline
              disabled={showAudienceCheckPreview}
              className="CampaignAudience__filters-preview-audience-btn"
              onClick={() => {
                setShowAudienceCheckPreview(true)
              }}
            >
              <Trans>Preview audience</Trans>
            </ButtonOutline>
            {(campaign.is_active || isDirty) && (
              <>
                <NbSp />
                <SaveCampaignFiltersButton
                  // @ts-expect-error 9/11/2020
                  filters={filters}
                  campaignId={campaign.id}
                />
              </>
            )}
          </div>
          <div className="CampaignAudience__filters-audience-check-preview">
            {showAudienceCheckPreview && (
              <CampaignAudienceCheckPreview
                campaignId={campaign.id}
                // @ts-expect-error 9/11/2020
                filters={filters}
              />
            )}
          </div>
        </div>
      )}
      {schedule_type === CampaignScheduleEnum.IMMEDIATE ? (
        <CampaignImmediateAudienceTable campaignId={campaign.id} />
      ) : (
        <CampaignAudienceTable campaignId={campaign.id} />
      )}
    </div>
  )
}
